import React, { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { Input } from '../../components/ui/input';
import { Button } from '../Components/Button';
import { MUZOLOGY_APP } from '@/lib/defaults';
import { beginCheckout } from '@/components/Checkout/utils/CheckoutProcess';
import { handlePreCheckout } from '@/lib/checkout_helpers/handlePreCheckout';
import { submitDuplicateAccountCheck } from '@/lib/checkout_helpers/submitDuplicateAccountCheck';

import { useAnalytics } from '@/analytics/AnalyticsProvider';
import { useForm } from 'react-hook-form';
import { checkValidEmail } from '@/lib/muzology';
import RedirectModal from '@/components/RedirectModal/RedirectModal';
import { trackEvent, trackConversion } from '@/lib/posthog';
import { getPageContext } from '@/lib/posthog-helpers';

const EmailSignupField = ({
    id = 'email-signup',
    className = null,
    inputClassName = null,
    borderColor = 'white',
    textColor = 'white',
    setRedirectModal,
    setInactiveAccount
}) => {
    const router = useRouter();
    const analytics = useAnalytics();
    const [isLoading, setLoading] = useState(false);
    const [hasTrackedFocus, setHasTrackedFocus] = useState(false);

    const defaultValues = {
        email: null,
    };

    if (process.env.NEXT_PUBLIC_FILL_TEST_DATA) {
        defaultValues.email = `test-${Date.now()}@muzology.com`;
    }
    const {
        register,
        handleSubmit,
        watch,
        setError,
        formState: { errors }
    } = useForm({ defaultValues });


    const onSubmitBeginCheckout = useCallback(
        (data) => {
            const config = {
                data,
                router,
                analytics,
                beginCheckout,
                setLoading,
                setError,
                id
            };
            return handlePreCheckout(config);
        },
        [analytics, id, router, setError]
    );

    // PostHog Tracking: Track email input focus
    const handleInputFocus = useCallback(() => {
        if (!hasTrackedFocus) {
            console.log('[PostHog] Email signup input focused');
            trackEvent('email_signup_input_focus', {
                element_type: 'input',
                element_id: id,
                section: 'hero',
                action: 'email_input_focused',
                ...getPageContext(),
            });
            setHasTrackedFocus(true);
        }
    }, [hasTrackedFocus, id]);

    // PostHog Tracking: Track button click
    const handleButtonClick = useCallback(() => {
        console.log('[PostHog] Email signup button clicked');
        trackEvent('email_signup_button_click', {
            element_type: 'button',
            element_text: 'Get Started',
            section: 'hero',
            action: 'signup_button_clicked',
            ...getPageContext(),
        });
    }, []);

    const onSubmitDuplicateAccountCheck = useCallback(
        async (data) => {
            const { email } = data;

            // PostHog Tracking: Track form submission attempt (high-value conversion)
            console.log('[PostHog] Email signup form submitted');
            trackConversion('email_signup_form_submit', {
                form_id: id,
                form_name: 'Email Signup',
                section: 'hero',
                action: 'form_submitted',
                ...getPageContext(),
            });

            const config = {
                data,
                analytics,
                setLoading,
                setError,
                setRedirectModal,
                setInactiveAccount,
                email,
                router
            };

            try {
                // Call the duplicate account check which now returns a boolean success indicator
                const success = await submitDuplicateAccountCheck(config);

                // Only call handlePreCheckout if the duplicate check was successful
                if (success) {
                    return onSubmitBeginCheckout(data);
                }
                // If not successful, just return (the error handling is done in submitDuplicateAccountCheck)
                return false;
            } catch (error) {
                // Additional error handling if needed
                console.error('Error in duplicate account check:', error);
                return false;
            }
        },
        [analytics, setError, setRedirectModal, setInactiveAccount, router, onSubmitBeginCheckout, id]
    );


    return (
        <form onSubmit={handleSubmit(onSubmitDuplicateAccountCheck)} className={className}>
            <Input
                type='email'
                placeholder='Enter Your Email'
                name='email'
                onFocus={handleInputFocus}
                className={inputClassName ?? `max-w-[260px] md:max-w-[300px] pl-0 md:pl-4 py-[26px] border-[1px] border-solid border-${borderColor} text-${textColor} font-normal rounded-full text-center md:text-left font-Lexend text-lg`}
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
                {...register('email', { required: true, validate: { validEmail: checkValidEmail } })}
            />
            <div className='w-full max-w-[260px] md:max-w-[180px]'>
                <Button
                    type='submit'
                    variant='primary'
                    className='w-full md:max-w-[180px] px-6 py-4 text-lg'
                    onClick={handleButtonClick}
                >
                    Get Started
                </Button>
            </div>

        </form>
    );
};

export default EmailSignupField;
